/**
 * État du menu `@` ouvert — registre module-level.
 *
 * Un seul menu peut être ouvert à la fois (celui de l'éditeur qui a le focus).
 * Le composant de menu est monté dans **chaque** éditeur rich text, mais
 * `getVariableSuggestionFor(owner)` rend `null` — une valeur stable — à tous
 * ceux qui ne sont pas propriétaires : `useSyncExternalStore` compare avec
 * `Object.is` et ne les re-rend donc jamais. Seul l'éditeur actif re-rend, et
 * seulement sur son propre sous-arbre de menu (invariants §4 du README).
 */

import { normalizeForSearch } from './variableCatalog';

/** Au-delà, le menu est illisible et la liste coûte trop cher à rendre : on
 *  invite à préciser la recherche plutôt que de dérouler 400 lignes. */
export const VARIABLE_SUGGESTION_LIMIT = 60;

let state = null;
const listeners = new Set();

/**
 * Liste affichée : les variables retenues par le champ de recherche du menu,
 * tronquées. Le filtre du champ s'applique **après** celui de la frappe `@`, et
 * porte aussi sur le nom du groupe (« offre nom » trouve « Offre / Nom »).
 */
function visibleItems(allItems, search) {
  const tokens = normalizeForSearch(search).split(/\s+/).filter(Boolean);
  const matched = tokens.length
    ? allItems.filter((variable) => {
      const haystack = normalizeForSearch(`${variable.groupLabel} ${variable.name}`);
      return tokens.every((token) => haystack.includes(token));
    })
    : allItems;
  return matched.slice(0, VARIABLE_SUGGESTION_LIMIT);
}

function emit() {
  listeners.forEach((listener) => listener());
}

export function subscribeVariableSuggestion(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** État du menu pour cet éditeur, ou `null` (référence stable) s'il n'est pas actif. */
export function getVariableSuggestionFor(owner) {
  return state && state.owner === owner ? state : null;
}

export function getVariableSuggestionState() {
  return state;
}

/**
 * Ouvre ou met à jour le menu.
 * L'index de sélection est conservé s'il reste dans la liste, remis à 0 sinon
 * (la liste se réduit à mesure qu'on tape).
 */
export function openVariableSuggestion({
  owner, items, clientRect, onSelect,
}) {
  const sameOwner = state && state.owner === owner;
  const previousIndex = sameOwner ? state.selectedIndex : 0;
  // La recherche saisie dans le menu survit à la frappe dans l'éditeur : les
  // deux filtres se cumulent tant que le même menu reste ouvert.
  const search = sameOwner ? state.search : '';
  const allItems = items || [];
  const shown = visibleItems(allItems, search);
  state = {
    owner,
    allItems,
    items: shown,
    search,
    clientRect: clientRect || null,
    onSelect,
    selectedIndex: previousIndex < shown.length ? previousIndex : 0,
  };
  emit();
}

/** Champ de recherche du menu — même rôle que l'`Input.Search` de l'éditeur classique. */
export function setVariableSuggestionSearch(search) {
  if (!state || state.search === search) return;
  state = {
    ...state,
    search,
    items: visibleItems(state.allItems, search),
    selectedIndex: 0,
  };
  emit();
}

export function closeVariableSuggestion(owner) {
  if (owner && state && state.owner !== owner) return;
  if (!state) return;
  state = null;
  emit();
}

/** Navigation clavier : déplacement circulaire dans la liste à plat. */
export function moveVariableSuggestionSelection(delta) {
  if (!state || !state.items.length) return false;
  const count = state.items.length;
  const next = (state.selectedIndex + delta + count) % count;
  state = { ...state, selectedIndex: next };
  emit();
  return true;
}

export function setVariableSuggestionSelection(index) {
  if (!state || index === state.selectedIndex) return;
  if (index < 0 || index >= state.items.length) return;
  state = { ...state, selectedIndex: index };
  emit();
}

/** Insère la variable sélectionnée. Rend `false` si rien n'est sélectionnable. */
export function commitVariableSuggestionSelection() {
  if (!state || !state.items.length) return false;
  const variable = state.items[state.selectedIndex];
  const { onSelect } = state;
  if (!variable || typeof onSelect !== 'function') return false;
  onSelect(variable);
  return true;
}

/** Remise à zéro — réservée aux tests. */
export function resetVariableSuggestionRegistry() {
  state = null;
  listeners.clear();
}
